import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { announcementsApi } from "@/lib/api/announcements";
import { useEffectiveFeatures } from "@/lib/features";
import { hasCheckedLoginPopup, markLoginPopupChecked } from "@/lib/loginPopup";
import { can, useAuthStore } from "@/stores/auth-store";
import type { Announcement } from "@/types/domain";

import { AnnouncementPopup, type PopupContent } from "./AnnouncementPopup";
import { openAnnouncementLink } from "./announcementLink";

/** Small delay so the popup lookup never competes with the dashboard's own
 * first requests - login itself has already completed by now. */
const FETCH_DELAY_MS = 400;

/**
 * Mounted once in AppShell, i.e. only after authentication has succeeded.
 * Asks the backend for this user's active, unseen, targeted content once per
 * signed-in tab session and shows it as a carousel. Every failure is
 * swallowed: the popup is a courtesy, never a gate - the user is already on
 * their dashboard whether or not it appears.
 */
export function LoginAnnouncementsPopup() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { data: features } = useEffectiveFeatures();
  const [items, setItems] = useState<Announcement[]>([]);
  const [open, setOpen] = useState(false);
  const viewed = useRef(new Set<string>());

  const featureKnown = Array.isArray(features);
  const featureOn = featureKnown && (features.find((f) => f.key === "announcement_module")?.enabled ?? true);
  const eligible = !!user && !user.is_super_admin && featureOn && can("announcement.view");
  const userId = user?.id;

  useEffect(() => {
    if (!eligible || !userId || hasCheckedLoginPopup(userId)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      announcementsApi
        .loginPopup()
        .then((list) => {
          markLoginPopupChecked(userId);
          if (cancelled || !Array.isArray(list) || list.length === 0) return;
          setItems(list);
          setOpen(true);
        })
        .catch(() => {
          // Never block or surface an error: the dashboard is already up.
          markLoginPopupChecked(userId);
        });
    }, FETCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [eligible, userId]);

  const onSlideShown = useCallback((item: PopupContent) => {
    if (viewed.current.has(item.id)) return;
    viewed.current.add(item.id);
    announcementsApi.markPopupViewed(item.id).catch(() => undefined);
  }, []);

  const finish = useCallback(
    (current: PopupContent) => {
      setOpen(false);
      announcementsApi
        .dismissPopup(current.id)
        .catch(() => undefined)
        .finally(() => queryClient.invalidateQueries({ queryKey: ["announcements"] }));
    },
    [queryClient],
  );

  const onCta = useCallback(
    (item: PopupContent) => {
      finish(item);
      openAnnouncementLink(item.cta_url, navigate);
    },
    [finish, navigate],
  );

  if (items.length === 0) return null;
  return <AnnouncementPopup items={items} open={open} onClose={finish} onSlideShown={onSlideShown} onCta={onCta} />;
}
